import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { InvoiceDetail } from '@faktorplus/shared';
import { Button } from '../../components/ui/button';
import { ErrorBox } from '../../components/ui/feedback';
import { ApiError, apiRequest, uploadAttachment } from '../../lib/api-client';
import { queryKeys } from '../../lib/query-keys';
import styles from './invoice-detail.module.css';

/**
 * Manages the scans on an invoice.
 *
 * Always rendered for office roles, not only when the invoice has no file: a
 * blurry photo has to be replaceable, and a two-page invoice needs a second
 * upload. Both were impossible while this only appeared on empty invoices.
 */
export function AttachmentUpload({ invoice }: { invoice: InvoiceDetail }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState<'upload' | 'delete' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.invoices.detail(invoice.id) });

  async function handleFile(file: File | undefined) {
    if (!file) return;

    setError(null);
    setBusy('upload');
    try {
      await uploadAttachment(invoice.id, file);
      await refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Nie udało się wgrać pliku.');
    } finally {
      setBusy(null);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  async function handleDelete(attachmentId: string) {
    setError(null);
    setBusy('delete');
    try {
      await apiRequest<void>(`/attachments/${attachmentId}`, { method: 'DELETE' });
      await refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Nie udało się usunąć pliku.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className={styles.actionCard}>
      <p className={styles.actionTitle}>
        {invoice.attachments.length === 0 ? 'Skan faktury' : 'Załączniki'}
      </p>

      {invoice.attachments.length > 0 && (
        <ul className={styles.fileList}>
          {invoice.attachments.map((attachment) => (
            <li key={attachment.id} className={styles.fileRow}>
              <span className={styles.fileName}>{attachment.originalName}</span>
              <button
                type="button"
                className={styles.fileRemove}
                disabled={busy !== null}
                onClick={() => void handleDelete(attachment.id)}
                aria-label={`Usuń ${attachment.originalName}`}
              >
                Usuń
              </button>
            </li>
          ))}
        </ul>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,image/*"
        className="visually-hidden"
        onChange={(event) => void handleFile(event.target.files?.[0])}
      />

      <Button
        isLoading={busy === 'upload'}
        disabled={busy !== null}
        onClick={() => inputRef.current?.click()}
      >
        {invoice.attachments.length === 0 ? 'Wgraj plik' : 'Dodaj kolejny plik'}
      </Button>

      {error && <ErrorBox>{error}</ErrorBox>}
    </div>
  );
}
